import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
  calendarAvailabilitiesThunk,
  getCandidateCalendarData,
} from "../../../../store/candidate/thunks";
import {
  TDayAvailabilityData,
  TDayData,
  TSaveCalendarDataInput,
} from "../../../../store/candidate/types";
import CustomButton from "../../../UI/Buttons/CustomButton";
import MyCalendar from "../../../UI/Calendar/MyCalendar";
import EditPencil from "../../../UI/Icons/EditPencil";
import CandidateJobHeader from "../CandidateJobHeader";
import RadioInput from "../../../UI/Inputs/RadioInput";
import KmSelector from "../../../UI/KmSelector";
import { setMaxDistanceAwayForMyCalendarAction } from "../../../../store/candidate/slice";
import ExpiringNotifications from "../Elements/ExpiringNotification";

const CalendarView: FC = () => {
  const dispatch = useDispatch();

  const {
    candidateCalendarData,
    candidateAcceptedJobsList: candidateJobsList,
    pendingJobCount,
    acceptedJobCount,
  } = useSelector((state) => state.candidateJobs);
  const verificationStatus = useSelector(
    (state) => state.onboarding.personaldetails?.verification_status
  );
  const jobpreferences = useSelector(
    (state) => state.onboarding.jobpreferences
  );
  const documentsExpired = useSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );

  const [showAvailabilitiesmodal, setShowAvailabilitiesModal] =
    useState<boolean>(false);
  const [selectedDaysResetTrigger, setSelectedDaysResetTrigger] =
    useState(false);
  const [isAllDayAvailable, setIsAllDayAvailable] =
    useState<boolean>(false);
  const [isAllDayNotAvailable, setIsAllDayNotAvailable] =
    useState<boolean>(false);
  const [selectDistanceAway, setSelectDistanceAway] = 
    useState<number>(jobpreferences?.max_travel_distance ?? 10);
  
  useEffect(() => {
    dispatch(setMaxDistanceAwayForMyCalendarAction(selectDistanceAway))
  }, [dispatch, selectDistanceAway])

  const loadCalendarData = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCandidateCalendarData({ distance_away: selectDistanceAway}));
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch, selectDistanceAway]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const redirectToAvailabilities = () => {
    setShowAvailabilitiesModal(true);
  };

  const handleSaveAvailabilities = async (
    inputData: TSaveCalendarDataInput
  ) => {
    dispatch(showTransparentPreloaderAction());
    const response = await dispatch(calendarAvailabilitiesThunk(inputData));
    if (isFulfilled(response)) {
      loadCalendarData && loadCalendarData(); // Reload calendar data if the function is provided
      setSelectedDaysResetTrigger(true); // Trigger reset
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const getAcceptedJobDates = (calendarData: any): string[] => {
    const accepted_job_dates: string[] = [];
    for (const weekKey in calendarData) {
      const week = calendarData[weekKey];
      for (const dayKey in week) {
        const day = week[dayKey];
        if (day.accepted_count >= 1) {
          accepted_job_dates.push(day.date);
        }
      }
    }
    return accepted_job_dates;
  };

  const bulkChangeAllDaysToAvailable = (checked: boolean) => {
    const inputData: TSaveCalendarDataInput = {
      type: "bulk",
      is_available: 1,
    };
    if (checked) {
      handleSaveAvailabilities(inputData);
    }
  };

  const bulkChangeAllDaysToNotAvailable = (checked: boolean) => {
    const inputData: TSaveCalendarDataInput = {
      type: "bulk",
      is_available: 0,
      accepted_job_dates: getAcceptedJobDates(candidateCalendarData),
    };
    if (checked) {
      handleSaveAvailabilities(inputData);
    }
  };


  const { allCheckedTrue, allCheckedFalse, hasAcceptedJobs } = useMemo(() => {
    const statusList: ("available" | "not_available" | "partial")[] = [];
    let hasAcceptedJobs = false;

    for (const weekKey in candidateCalendarData) {
      const week = candidateCalendarData[weekKey];
      for (const dayKey in week) {
        const day: TDayData = week[dayKey];

        if (day.accepted_count > 0) {
          hasAcceptedJobs = true;
        }

        const isTrue =
          day?.checked === true &&
          Array.isArray(day.availability) &&
          day.availability.length > 0 &&
          day.availability.every(
            (a: TDayAvailabilityData) => a.all_day_available === 1
          );

        const isFalse =
          day?.checked === false &&
          Array.isArray(day.availability) &&
          day.availability.length > 0 &&
          day.availability.every(
            (a: TDayAvailabilityData) => a.all_day_available === 0
          );

        if (isTrue) statusList.push("available");
        else if (isFalse) statusList.push("not_available");
        else statusList.push("partial");
      }
    }

    const allCheckedTrue =
      statusList.length > 0 && statusList.every((s) => s === "available");
    const allCheckedFalse =
      statusList.length > 0 && statusList.every((s) => s === "not_available");

    return { allCheckedTrue, allCheckedFalse, hasAcceptedJobs };
  }, [candidateCalendarData]);

  return (
    <div className="d-flex flex-column gap-20">
      { verificationStatus && verificationStatus === "verified" ? 
        <>
          <ExpiringNotifications />
          <div className="d-flex gap-3 justify-content-end align-items-center">
            <div className="fs-13 fw-400 text-black">Maximum distance away</div>
            <KmSelector 
              selectedDistancyAway={selectDistanceAway}
              setSelectedDistancyAway={setSelectDistanceAway}
            />
          </div>
        </> : "" 
      }
      <CandidateJobHeader
        jobListingCount={0}
        pendingJobCount={pendingJobCount}
        acceptedJobCount={acceptedJobCount}
        isCandidateFilledAllForms={true}
        documentsExpired={documentsExpired}
      />

      <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
        <div className="fs-22 fw-400 text-black">Upcoming 5 Weeks</div>
        <div className="d-flex gap-20 align-items-center">
          <div className="d-none d-lg-flex flex-column gap-3 fs-10 fw-400 text-end">
            <div className="text-black">
              Bulk change availability for entire 5 weeks
            </div>
            <div className="text-grey-medium">
              Only days without jobs will be changed
            </div>
          </div>

          <div className="theme-check d-none d-lg-block">
            <RadioInput
              label="Not available"
              name="bulkAvailability"
              value="not_available"
              checked={isAllDayNotAvailable}
              onChange={() => {
                bulkChangeAllDaysToNotAvailable(true);
                setIsAllDayNotAvailable(true);
                setIsAllDayAvailable(false);
              }}
              // disabled={hasAcceptedJobs} // No need to hide this option
              themeCheck={true}
            />
          </div>
          <div className="theme-check d-none d-lg-block">
            <RadioInput
              label="Available all day"
              name="bulkAvailability"
              value="available"
              checked={isAllDayAvailable}
              onChange={() => {
                bulkChangeAllDaysToAvailable(true);
                setIsAllDayAvailable(true);
                setIsAllDayNotAvailable(false);
              }}
              themeCheck={true}
            />
          </div>
          <CustomButton
            variant="primary"
            type="button"
            className="btn-theme bg-teal text-black max-h-30px mobile-w-100"
            onClick={redirectToAvailabilities}
          >
            <EditPencil />
            Bulk Change
          </CustomButton>
        </div>
      </div>

      <MyCalendar
        handleSaveAvailabilities={handleSaveAvailabilities}
        loadCalendarData={loadCalendarData}
        candidateCalendarData={candidateCalendarData}
        showAvailabilitiesmodal={showAvailabilitiesmodal}
        setShowAvailabilitiesModal={setShowAvailabilitiesModal}
        selectedDaysResetTrigger={selectedDaysResetTrigger}
        setSelectedDaysResetTrigger={setSelectedDaysResetTrigger}
      />
    </div>
  );
};

export default CalendarView;
